import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';

export type EmployeeRole = 'manager' | 'receptionist' | 'analyst';
export type EmployeePermissionMap = Record<string, string[]>;
export type NotificationBucket = 'reservations' | 'business';
export interface NotificationBucketOption { key: NotificationBucket; label: string; description: string; }

export interface EmployeeUser {
  user_id: number;
  name: string;
  email: string;
  phone_number?: string | null;
  force_password_change: boolean;
  last_login_at?: string | null;
  created_at: string;
}

export interface Employee {
  user_id: number;
  business_role: EmployeeRole;
  job_title?: string | null;
  permissions: EmployeePermissionMap;
  // null = sin configurar todavía (recibe todos los avisos por correo,
  // mismo comportamiento que antes de que existiera este selector).
  notification_categories: NotificationBucket[] | null;
  is_active: boolean;
  user: EmployeeUser;
}

export interface EmployeeCatalog {
  catalog: EmployeePermissionMap;
  role_templates: Record<EmployeeRole, EmployeePermissionMap>;
  notification_buckets: NotificationBucketOption[];
}

export interface EmployeeFormPayload {
  name: string;
  last_name?: string;
  email: string;
  phone_number?: string;
  job_title?: string;
  business_role?: EmployeeRole;
  password?: string;
  permissions?: EmployeePermissionMap;
  notification_categories?: NotificationBucket[];
}

/** Propietario delegado: mismo acceso operativo que el propietario, sin permisos granulares que editar. */
export interface DelegatedOwner {
  user_id: number;
  job_title?: string | null;
  is_active: boolean;
  user: EmployeeUser;
}

export interface DelegatedOwnerFormPayload {
  name: string;
  last_name?: string;
  email: string;
  phone_number?: string;
  job_title?: string;
  password?: string;
}

@Injectable({ providedIn: 'root' })
export class EmployeeService {
  private readonly url = `${environment.apiUrl}/employees`;
  constructor(private http: HttpClient) {}
  catalog(businessId: number) { return this.http.get<EmployeeCatalog>(`${this.url}/permissions/catalog`, { params: { business_id: String(businessId) } }); }
  changeMyPassword(current_password: string, new_password: string) { return this.http.patch(`${this.url}/change-my-password`, { current_password, new_password }); }
  list(businessId: number, search = '', status = '', page = 1, limit = 12) {
    let params = new HttpParams().set('page', page).set('limit', limit);
    if (search) params = params.set('search', search);
    if (status) params = params.set('status', status);
    return this.http.get<{ total: number; page: number; pages: number; data: Employee[] }>(`${this.url}/business/${businessId}`, { params });
  }
  create(businessId: number, body: EmployeeFormPayload) { return this.http.post<{ employee: Employee }>(`${this.url}/business/${businessId}`, body); }
  update(businessId: number, userId: number, body: EmployeeFormPayload) { return this.http.put<{ employee: Employee }>(`${this.url}/business/${businessId}/${userId}`, body); }
  status(businessId: number, userId: number, is_active: boolean) { return this.http.patch(`${this.url}/business/${businessId}/${userId}/status`, { is_active }); }
  resetPassword(businessId: number, userId: number) { return this.http.patch(`${this.url}/business/${businessId}/${userId}/reset-password`, {}); }
  delete(businessId: number, userId: number, reason: string) { return this.http.delete(`${this.url}/business/${businessId}/${userId}`, { body: { reason } }); }

  listOwners(businessId: number, search = '', status = '', page = 1, limit = 12) {
    let params = new HttpParams().set('page', page).set('limit', limit);
    if (search) params = params.set('search', search);
    if (status) params = params.set('status', status);
    return this.http.get<{ total: number; page: number; pages: number; data: DelegatedOwner[] }>(`${this.url}/business/${businessId}/owners`, { params });
  }
  createOwner(businessId: number, body: DelegatedOwnerFormPayload) { return this.http.post<{ owner: DelegatedOwner }>(`${this.url}/business/${businessId}/owners`, body); }
  ownerStatus(businessId: number, userId: number, is_active: boolean) { return this.http.patch(`${this.url}/business/${businessId}/owners/${userId}/status`, { is_active }); }
  resetOwnerPassword(businessId: number, userId: number) { return this.http.patch(`${this.url}/business/${businessId}/owners/${userId}/reset-password`, {}); }
  deleteOwner(businessId: number, userId: number, reason: string) { return this.http.delete(`${this.url}/business/${businessId}/owners/${userId}`, { body: { reason } }); }
}
